import Button from "@/common/elements/Button";
import DataTable from "@/common/elements/DataTable";
import { StyledFlex, StyledSubTableContainer } from "@/common/styles";
import CustomModal from "@/components/CustomModal";
import { useState, useRef } from "react";
import { Popconfirm } from "antd";
import OffenseWarrantChargeDetailForm from "./OffenseWarrantChargeDetailForm";
import useActiveWarrant from "@/hooks/useActiveWarrant";
import notificationService from "@/util/notificationService";
import useDialog from "@/hooks/useDialog";
import { getOptionsFromRefList } from "@/util/common_function";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const WARRANT_CHARGES_HEADERS = [
  { key: "id", text: "#" },
  // { key: "chargeNumber",      text: "Charge #"           },
  { key: "charge", text: "Charge" },
  { key: "chargeLevelID", text: "Level" },
  { key: "chargeCount", text: "Cnt" },
  { key: "controllingCharge", text: "Controlling Charge" },
  { key: "sealed", text: "Sealed" },
];

const OffenseInformationChargesDetails = () => {
  const { activeWarrant, bookingsFieldsEnabled, patchArray } =
    useActiveWarrant();
  const { showDialog } = useDialog();

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). This tab is only reachable
  // from the "Warrants New" module (single-warrant detail view), so the
  // gridId is hardcoded the same way Bookings' grids are.
  const {
    headers: tableHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Warrants New:WarrantCharges", WARRANT_CHARGES_HEADERS);

  const formRef = useRef(null); // ref to OffenseWarrantChargeDetailForm

  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // null = Add, number = Edit
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  // ── Derive rows from Redux ─────────────────────────────────────────────────
  const charges = activeWarrant?.warrantCharges ?? [];

  const chargeLevelOptions = getOptionsFromRefList(
    "Charge_Level",
    false,
    false,
    true,
  );

  const tableRows = charges.map((c, i) => {
    const chargeLevelLabel =
      chargeLevelOptions.find((o) => o.value === c.chargeLevelID)?.label ??
      c.chargeLevelID ??
      "";

    return {
      _index: i,
      id: i + 1,
      // chargeNumber:      c.chargeNumber      ?? "",
      charge: c.charge ?? "",
      chargeLevelID: chargeLevelLabel, // ← label instead of ID
      chargeCount: c.chargeCount ?? "",
      controllingCharge: c.controllingCharge ? "Yes" : "No",
      sealed: c.sealed ? "Yes" : "No",
    };
  });

  // ── Handlers ───────────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditingIndex(null);
    setShowModal(true);
  };

  const openDetails = () => {
    if (selectedRowIndex === null || selectedRowIndex === undefined) {
      notificationService.error("Please select a charge row first");
      return;
    }
    const charge = charges[selectedRowIndex];
    if (!charge) {
      notificationService.error("Selected charge not found");
      return;
    }
    setEditingIndex(selectedRowIndex);
    setShowModal(true);
  };

  // Single click → select only
  const handleRowClick = (data, index) => {
    // console.log("Index : ", index);
    const idx = typeof data?._index === "number" ? data._index : null;
    setSelectedRowIndex(idx);
    if (index !== undefined && index !== null) {
      if (idx === null) return;
      const charge = charges[idx];
      if (!charge) return;
      setEditingIndex(idx);
      setShowModal(true);
    }
  };

  /**
   * Called by CustomModal's footer Save button.
   * Pulls form data via ref, then merges into Redux.
   */
  const handleModalSave = () => {
    const charge = formRef.current?.getFormData();
    if (!charge) {
      notificationService.error("Fill out all mandatory fields.");
      return;
    }

    // ── Controlling charge conflict check ──────────────────────────────────
    if (charge.controllingCharge) {
      const conflictIndex = charges.findIndex(
        (c, i) => c.controllingCharge === true && i !== editingIndex,
      );

      if (conflictIndex !== -1) {
        const conflictCharge = charges[conflictIndex];
        const conflictChargeNum =
          conflictCharge?.chargeNumber || conflictIndex + 1;
        showDialog("info", {
          content: `Only One charge can be marked as the controlling charge for this person. You must clear the controlling flag for charge #${conflictChargeNum} first`,
          confirmButtonText: "Ok",
        });
        return; // wait for dialog response
      }
    }
    // ───────────────────────────────────────────────────────────────────────

    const updated = [...charges];
    if (editingIndex !== null) {
      updated[editingIndex] = { ...updated[editingIndex], ...charge };
    } else {
      updated.push(charge);
    }

    patchArray("warrantCharges", updated);
    setShowModal(false);
    setEditingIndex(null);
  };

  const handleDelete = async () => {
    if (selectedRowIndex === null || selectedRowIndex === undefined) {
      notificationService.error("Please select a charge to delete");
      return;
    }

    const canDelete = await new Promise((resolve) => {
      showDialog("confirm", {
        title: "Warning",
        content: "Are you sure you want to delete this charge?",
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!canDelete) return;

    const updated = charges.filter((_, i) => i !== selectedRowIndex);
    patchArray("warrantCharges", updated);
    setSelectedRowIndex(null);
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingIndex(null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <StyledSubTableContainer>
        <StyledFlex justify="space-between" align="center" marginBottom="5px">
          <h2 style={{ margin: "0" }}>Charges</h2>
          <StyledFlex gap="10px">
            <Button onClick={openAdd} disabled={!bookingsFieldsEnabled || isConfiguring}>
              Add
            </Button>
            <Button onClick={openDetails} disabled={selectedRowIndex === null || isConfiguring}>
              Details
            </Button>
            <Button
              onClick={handleDelete}
              disabled={selectedRowIndex === null || !bookingsFieldsEnabled || isConfiguring}
            >
              Delete
            </Button>

            {/* Config — drag-reorder/resize columns */}
            {!isConfiguring ? (
              <Button htmlType="button" onClick={startConfiguring}>
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Save the current column layout?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button htmlType="button">{configButtonLabel}</Button>
              </Popconfirm>
            )}
          </StyledFlex>
        </StyledFlex>

        <DataTable
          ref={dataTableRef}
          headers={tableHeaders}
          draggable={isConfiguring}
          data={tableRows}
          rowUniqueId="_index"
          onRowClick={handleRowClick}
          // onRowDoubleClick={handleRowDoubleClick}
        />
      </StyledSubTableContainer>

      {showModal && (
        <CustomModal
          show={showModal}
          onHide={handleCancel}
          header={editingIndex !== null ? "Edit Charge" : "Add Charge"}
          size="xl"
          onSaveClick={handleModalSave} // ← pulls data from formRef, no args needed
          submitButtonText="Save"
        >
          <OffenseWarrantChargeDetailForm
            ref={formRef}
            initialValues={editingIndex !== null ? charges[editingIndex] : null}
          />
        </CustomModal>
      )}
    </>
  );
};

export default OffenseInformationChargesDetails;
